import Backbone from "backbone";
import $ from "jquery";
import * as _ from "underscore";

import * as chromeExtensionInstaller from "chromeExtensionInstaller";
import * as deviceDetect from "deviceDetect";
import * as localAPI from "localAPI";
import * as Mustache from "mustache";
import * as mustcache from "mustcache";
import readlangClient from "readlangClient";
import * as languageMap from "shared/languageMap";
import * as signupModal from "signupModal";
import * as splitTest from "splitTest";
import * as walkthrough from "walkthrough";

/**
 * This allows us to present a button to the user allowing them to install Readlang as a PWA to
 * their home screen.
 */
let installPrompt: { prompt?: () => void } | undefined = undefined;
window.addEventListener("beforeinstallprompt", (event) => {
  event.preventDefault();
  installPrompt = event as { prompt?: () => void };
});

const WalkthroughChecklistView = Backbone.View.extend({
  tagName: "div",
  initialize: async function (options) {
    this.models = options.models;
    this.appRouter = options.appRouter;

    this.dismissible = !(await localAPI.isLocalMode());

    this.render();

    walkthrough.events.on("new", this.render.bind(this));
  },
  checklistItems: [
    {
      name: "Choose language to learn",
      html: 'Currently set to {{languageString}}, change or confirm this using the <strong class="currentLearningLanguageHint">I\'m learning</strong> dropdown at the top of the page.',
      achievement: "setLanguages",
      isActive: true,
    },
    /*
    {
      name: "Sign up to Readlang",
      html: '<a style="margin-left:20px" data-action="showSignupModal" href="#">Click here to signup</a>',
      isComplete: async function () {
        return !(await readlangClient.isLocalMode());
      },
      isActive: true,
    },
    */
    {
      name: "Read something",
      html:
        "<div style='margin-left:20px'>" +
        '{{#starterStories}}Read the starter story <a href="/library/{{easy}}">Arrival</a> (easy) or ' +
        '<a href="/library/{{medium}}">Vacation</a> (medium)' +
        "{{/starterStories}}" +
        '{{^starterStories}}Read from <strong class="libraryHint"><i class="icon-book"></i> Library</strong> tab above</li>' +
        "{{/starterStories}}" +
        "</div>",
      achievement: "translate",
      isActive: true,
    },
    {
      name: "Get the full app experience",
      html: installPrompt
        ? // Button to install as PWA
          `<a href="#" data-action="installPWA">Install Readlang to your Home Screen</a>`
        : deviceDetect.is("iOS")
          ? "<ol style='margin-left:20px'>" +
            `<li>Tap your browser's "Share" button <i class="icon-share"/> next to the address bar</li>` +
            '<li>Choose "Add to Home Screen"</li>' +
            "<li>Open Readlang from your iOS home screen</li>" +
            "</ol>"
          : "<ol style='margin-left:20px'>" +
            "<li>Open your browser's menu</li>" +
            '<li>Choose "Add to home screen"</li>' +
            "<li>Open Readlang from your Android home screen</li>" +
            "</ol>",

      achievement: "standalone",
      isActive: async () => {
        return (
          !(await readlangClient.isLocalMode()) &&
          (deviceDetect.is("iOS") || deviceDetect.is("android"))
        );
      },
    },
    {
      name: "Upload your own text or e-book",
      html:
        "<ol style='margin-left:20px'>" +
        '<li>Visit the <strong class="libraryHint"><i class="icon-book"></i> Library</strong> tab above</li>' +
        '<li>Click the <strong><i class="icon-circle-arrow-up"></i> Upload Text</strong> button</li>' +
        "</ol>",
      achievement: "uploadText",
      isActive: true,
    },
    {
      name: "Use the Web Reader",
      html:
        "<ol style='margin-left:20px'>" +
        '<li><a href="#" data-action="installWebReader">Install the Web Reader</a></li>' +
        "<li>Visit any {{user.currentLearningLanguageEnglishString}} webpage " +
        '(see <a href="/links">suggested sites</a>)</li>' +
        "<li>Use the Web Reader on that page</li>" +
        "</ol>",
      achievement: "webReader",
      isActive: true,
    },
    {
      name: "Practice flashcards",
      html:
        "<div style='margin-left:20px'>" +
        'Open the <strong class="flashcardsHint">Practice</strong> tab above' +
        "</div>",
      achievement: "flashcards",
      isActive: async function () {
        return walkthrough.hasAchievement("translate");
      },
    },
  ],
  reorderItems: function (items) {
    // shift completed items before all uncompleted items
    let firstUncompletedIndex;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];

      if (typeof firstUncompletedIndex === "undefined") {
        if (!item.complete) {
          firstUncompletedIndex = i;
        }
      } else {
        if (item.complete) {
          // move it immediately before the first uncompleted one
          items.splice(i, 1);
          items.splice(firstUncompletedIndex, 0, item);
          firstUncompletedIndex++;
        }
      }
    }
  },
  render: async function () {
    const templateData: any = {};

    const items = this.checklistItems;

    templateData.items = [];

    for (const item of items) {
      templateData.items.push({
        name: item.name,

        html: Mustache.render(item.html, {
          user: this.models.user.viewJSON(),
          languageString:
            languageMap.map[this.models.user.get("currentLearningLanguage")]
              .english,
          starterStories:
            languageMap.map[this.models.user.get("currentLearningLanguage")]
              .starterStories,
        }),

        complete: item.isComplete
          ? await item.isComplete()
          : walkthrough.hasAchievement(item.achievement),

        active:
          typeof item.isActive === "function"
            ? await item.isActive()
            : item.isActive,

        youTubeURL: item.youTubeURL,
        action: item.action,
      });
    }

    this.reorderItems(templateData.items);

    const allComplete = _.every(
      templateData.items,
      (item) => !item.active || item.complete
    );

    if (this.models.user.get("hideWalkthrough")) {
      this.$el.remove();
      splitTest.trackEventWithProperties("walkthrough", {
        complete: allComplete,
        hidden: true,
      });
      return;
    }

    if (allComplete) {
      splitTest.trackEventWithProperties("walkthrough", {
        complete: true,
      });

      // Remove if dismissible
      if (this.dismissible) {
        this.$el.remove();
        return;
      }
    } else {
      splitTest.trackEventWithProperties("walkthrough", {
        complete: false,
      });
    }

    templateData.completedCount = _.filter(
      templateData.items,
      (item) => item.complete && item.active
    ).length;
    templateData.totalCount = templateData.items.filter(
      (item) => item.active
    ).length;

    templateData.closeButton = this.dismissible;

    mustcache.render("walkthroughChecklist", templateData, (html) => {
      this.$el.html(html);
      this.delegateEvents();

      this.$(".libraryHint").hover(
        () => {
          $("#navBookshelf").addClass("glow");
        },
        () => {
          $("#navBookshelf").removeClass("glow");
        }
      );
      this.$(".flashcardsHint").hover(
        () => {
          $("#navTest").addClass("glow");
        },
        () => {
          $("#navTest").removeClass("glow");
        }
      );
      this.$(".currentLearningLanguageHint").hover(
        () => {
          $("#editionDropdown").addClass("glow");
        },
        () => {
          $("#editionDropdown").removeClass("glow");
        }
      );
    });
  },
  events: {
    'click [data-action="showSignupModal"]': "showSignupModal",
    'click [data-action="installWebReader"]': "installWebReader",
    'click [data-action="installPWA"]': "installPWA",
    "click .closeButton": "close",
  },
  showSignupModal: function (event) {
    event.preventDefault();
    event.stopPropagation();

    signupModal.show({
      headingHtml: "<h2>Let's get started!</h2>",
      models: this.models,
    });
  },
  installWebReader: function (event) {
    event.preventDefault();
    event.stopPropagation();

    // cspell: disable-next-line
    if (window.chrome && window.chrome.webstore) {
      chromeExtensionInstaller.install();
    } else {
      this.appRouter.navigate("/webReader", { trigger: true });
    }
  },
  installPWA: async function () {
    await installPrompt?.prompt?.();
  },
  close: function () {
    this.$el.remove();

    // TODO: show message saying that to find the checklist again, go to the dropdown menu
    // at the top right
    this.models.user.save(
      {
        hideWalkthrough: true,
      },
      { patch: true }
    );
  },
});

export default WalkthroughChecklistView;
